import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { StableInput, StableTextarea } from '../components/StableInput';
import { Label } from '../components/ui/label';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Plus, 
  Users, 
  Phone, 
  Mail, 
  Calendar,
  DollarSign,
  User,
  Thermometer,
  MessageSquare,
  Coffee,
  HandHeart,
  Trash2,
  Edit2,
  ChevronRight,
  Lock
} from 'lucide-react';

const TOUCHPOINT_TYPES = [
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone/Text', icon: Phone },
  { value: 'in_person', label: 'In Person', icon: Coffee },
  { value: 'ask', label: 'The Ask', icon: HandHeart },
];

const STATUS_COLORS = {
  cold: 'bg-blue-100 text-blue-700',
  warm: 'bg-orange-100 text-orange-700',
  hot: 'bg-red-100 text-red-700',
};

export default function Prospects() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState({ total: 0, cold: 0, warm: 0, hot: 0, total_estimated: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isTouchpointDialogOpen, setIsTouchpointDialogOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    status: 'cold',
    notes: '',
    estimated_gift: '',
    assigned_staff: '',
  });
  const [touchpointData, setTouchpointData] = useState({
    type: 'email',
    description: '',
    outcome: '',
    next_action: '',
    next_action_date: '',
  });

  const isPro = user?.subscription_plan === 'pro' || user?.subscription_plan === 'trial' || user?.subscription_plan === 'grants_pro' || user?.is_lifetime_pro;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (isPro) {
      fetchProspects();
      fetchStats();
    }
  }, [user, isPro, authLoading, navigate]);

  const fetchProspects = async (status = null) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const url = status 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/prospects?status=${status}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/prospects`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setProspects(data);
      }
    } catch (error) {
      console.error('Error fetching prospects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleAddProspect = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          estimated_gift: formData.estimated_gift ? parseFloat(formData.estimated_gift) : null,
        }),
      });

      if (response.ok) {
        toast.success('Prospect added successfully');
        setIsAddDialogOpen(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          status: 'cold',
          notes: '',
          estimated_gift: '',
          assigned_staff: '',
        });
        fetchProspects();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add prospect');
      }
    } catch (error) {
      console.error('Error adding prospect:', error);
      toast.error('Failed to add prospect');
    }
  };

  const handleUpdateStatus = async (prospectId, newStatus) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchProspects();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating prospect:', error);
    }
  };

  const handleDeleteProspect = async (prospectId) => {
    if (!confirm('Are you sure you want to delete this prospect?')) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Prospect deleted');
        fetchProspects();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting prospect:', error);
    }
  };

  const handleAddTouchpoint = async () => {
    if (!selectedProspect) return;
    
    try {
      const token = localStorage.getItem('pitchpal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/${selectedProspect.id}/touchpoints`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          prospect_id: selectedProspect.id,
          ...touchpointData,
        }),
      });

      if (response.ok) {
        toast.success('Touchpoint added');
        setIsTouchpointDialogOpen(false);
        setTouchpointData({
          type: 'email',
          description: '',
          outcome: '',
          next_action: '',
          next_action_date: '',
        });
        fetchProspects();
      }
    } catch (error) {
      console.error('Error adding touchpoint:', error);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    if (value === 'all') {
      fetchProspects();
    } else {
      fetchProspects(value);
    }
  };

  if (!isPro) {
    return (
      <div className="min-h-screen pt-24 pb-16 bg-[#FAFAFA]" data-testid="prospects-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-1">
                Donor Pipeline
              </h1>
              <p className="text-[#5C6B67] text-sm">
                Track donor relationships, log touchpoints, and manage your outreach pipeline.
              </p>
            </div>
            <Button disabled className="opacity-50 cursor-not-allowed" data-testid="add-prospect-locked">
              <Lock className="w-4 h-4 mr-2" /> Add Prospect
            </Button>
          </div>

          {/* Upgrade Banner */}
          <div className="bg-gradient-to-r from-primary/5 to-amber-50 border border-primary/20 rounded-lg p-4 flex items-center justify-between mb-6" data-testid="upgrade-banner">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                <Lock className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-800">Donor Pipeline requires Growth plan</p>
                <p className="text-xs text-stone-500">Track donors, log touchpoints, and manage your fundraising pipeline</p>
              </div>
            </div>
            <Button size="sm" onClick={() => navigate('/pricing')} className="bg-primary hover:bg-primary-800 text-white" data-testid="upgrade-prospects-btn">
              Upgrade to Growth — $49/mo
            </Button>
          </div>

          {/* Preview Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {['Total Prospects', 'Active Outreach', 'Converted', 'Pipeline Value'].map((label) => (
              <Card key={label} className="bg-white/80">
                <CardContent className="pt-4 pb-3 text-center">
                  <p className="text-2xl font-bold text-stone-300">--</p>
                  <p className="text-xs text-stone-400 mt-1">{label}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Preview Table */}
          <Card className="bg-white/80">
            <CardContent className="pt-6">
              <div className="relative">
                <div className="opacity-40 pointer-events-none">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-4 text-sm text-stone-600">Organization</th>
                        <th className="text-left py-3 px-4 text-sm text-stone-600">Contact</th>
                        <th className="text-left py-3 px-4 text-sm text-stone-600">Status</th>
                        <th className="text-left py-3 px-4 text-sm text-stone-600">Est. Value</th>
                        <th className="text-left py-3 px-4 text-sm text-stone-600">Last Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['Acme Foundation', 'City Community Fund', 'Green Earth Initiative'].map((name) => (
                        <tr key={name} className="border-b">
                          <td className="py-3 px-4 text-sm text-stone-400">{name}</td>
                          <td className="py-3 px-4 text-sm text-stone-300">contact@example.com</td>
                          <td className="py-3 px-4"><span className="text-xs bg-stone-100 text-stone-400 px-2 py-1 rounded-full">Lead</span></td>
                          <td className="py-3 px-4 text-sm text-stone-300">$5,000</td>
                          <td className="py-3 px-4 text-sm text-stone-300">Mar 20, 2026</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-stone-400 text-sm font-medium bg-white/80 px-4 py-2 rounded-lg">Sample data — upgrade to add real prospects</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAFAFA]" data-testid="prospects-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-[#0A0A0A] mb-2">
              Prospect Management
            </h1>
            <p className="text-stone-600">
              Track and manage your donor prospects and touchpoints.
            </p>
          </div>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary-800 text-white" data-testid="add-prospect-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Prospect
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Prospect</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Name *</Label>
                  <StableInput
                    value={formData.name}
                    onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="name"
                    placeholder="John Smith"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <StableInput
                      value={formData.email}
                      onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="email"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <StableInput
                      value={formData.phone}
                      onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="phone"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <Label>Organization</Label>
                  <StableInput
                    value={formData.organization}
                    onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="organization"
                    placeholder="ABC Company"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Status</Label>
                    <select
                      value={formData.status}
                      onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="status"
                      className="w-full h-10 px-3 border border-stone-200 rounded-md"
                    >
                      <option value="cold">Cold</option>
                      <option value="warm">Warm</option>
                      <option value="hot">Hot</option>
                    </select>
                  </div>
                  <div>
                    <Label>Estimated Gift ($)</Label>
                    <StableInput
                      type="number"
                      value={formData.estimated_gift}
                      onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="estimated_gift"
                      placeholder="5000"
                    />
                  </div>
                </div>
                <div>
                  <Label>Assigned Staff</Label>
                  <StableInput
                    value={formData.assigned_staff}
                    onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="assigned_staff"
                    placeholder="Jane Doe"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <StableTextarea
                    value={formData.notes}
                    onChange={(f,v) => setFormData(prev => ({ ...prev, [f]: v }))} field="notes"
                    placeholder="Any notes about this prospect..."
                    rows={3}
                  />
                </div>
                <Button onClick={handleAddProspect} className="w-full bg-primary hover:bg-primary-800 text-white">
                  Add Prospect
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
                  <p className="text-xs text-stone-500">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Thermometer className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.cold}</p>
                  <p className="text-xs text-stone-500">Cold</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                  <Thermometer className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.warm}</p>
                  <p className="text-xs text-stone-500">Warm</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                  <Thermometer className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.hot}</p>
                  <p className="text-xs text-stone-500">Hot</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-stone-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">${stats.total_estimated.toLocaleString()}</p>
                  <p className="text-xs text-stone-500">Pipeline</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Prospects List */}
        <Card className="bg-white border-stone-200">
          <CardHeader>
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className="bg-stone-100">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="cold">Cold</TabsTrigger>
                <TabsTrigger value="warm">Warm</TabsTrigger>
                <TabsTrigger value="hot">Hot</TabsTrigger>
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 spinner mx-auto" />
              </div>
            ) : prospects.length === 0 ? (
              <div className="text-center py-16" data-testid="prospects-empty">
                <div className="w-16 h-16 bg-primary-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">No prospects yet</h3>
                <p className="text-stone-600 mb-6 max-w-sm mx-auto">
                  Start tracking individuals who can support your mission — board members, major donors, and warm introductions.
                </p>
                <Button
                  onClick={() => setIsAddDialogOpen(true)}
                  className="bg-primary hover:bg-primary-800 text-white"
                  data-testid="prospects-empty-add-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add your first prospect
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {prospects.map((prospect) => (
                  <div 
                    key={prospect.id} 
                    className="border border-stone-200 rounded-lg p-4 hover:bg-[#FAFAFA] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-stone-900">{prospect.name}</h3>
                          <Badge className={STATUS_COLORS[prospect.status]}>
                            {prospect.status}
                          </Badge>
                          {prospect.estimated_gift && (
                            <span className="text-sm text-green-600 font-medium">
                              ${prospect.estimated_gift.toLocaleString()}
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-stone-500 mb-3">
                          {prospect.organization && (
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {prospect.organization}
                            </span>
                          )}
                          {prospect.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {prospect.email}
                            </span>
                          )}
                          {prospect.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {prospect.phone}
                            </span>
                          )}
                          {prospect.assigned_staff && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {prospect.assigned_staff}
                            </span>
                          )}
                        </div>

                        {prospect.notes && (
                          <p className="text-sm text-stone-600 mb-3">{prospect.notes}</p>
                        )}

                        {/* Touchpoints */}
                        {prospect.touchpoints && prospect.touchpoints.length > 0 && (
                          <div className="border-t border-stone-100 pt-3 mt-3">
                            <p className="text-xs font-medium text-stone-500 mb-2">Recent Touchpoints:</p>
                            <div className="space-y-2">
                              {prospect.touchpoints.slice(-3).reverse().map((tp) => {
                                const TypeIcon = TOUCHPOINT_TYPES.find(t => t.value === tp.type)?.icon || MessageSquare;
                                return (
                                  <div key={tp.id} className="flex items-start gap-2 text-sm">
                                    <TypeIcon className="w-4 h-4 text-stone-400 mt-0.5" />
                                    <div>
                                      <span className="text-stone-700">{tp.description}</span>
                                      {tp.outcome && (
                                        <span className="text-stone-500"> → {tp.outcome}</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        <select
                          value={prospect.status}
                          onChange={(e) => handleUpdateStatus(prospect.id, e.target.value)}
                          className="h-8 px-2 text-sm border border-stone-200 rounded-md"
                        >
                          <option value="cold">Cold</option>
                          <option value="warm">Warm</option>
                          <option value="hot">Hot</option>
                        </select>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedProspect(prospect);
                            setIsTouchpointDialogOpen(true);
                          }}
                          className="text-primary"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Touchpoint
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteProspect(prospect.id)}
                          className="text-stone-400 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Touchpoint Dialog */}
        <Dialog open={isTouchpointDialogOpen} onOpenChange={setIsTouchpointDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Touchpoint for {selectedProspect?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Type</Label>
                <select
                  value={touchpointData.type}
                  onChange={(e) => setTouchpointData({ ...touchpointData, type: e.target.value })}
                  className="w-full h-10 px-3 border border-stone-200 rounded-md"
                >
                  {TOUCHPOINT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Description *</Label>
                <StableTextarea
                  value={touchpointData.description}
                  onChange={(e) => setTouchpointData({ ...touchpointData, description: e.target.value })}
                  placeholder="What happened during this interaction?"
                  rows={3}
                />
              </div>
              <div>
                <Label>Outcome</Label>
                <StableInput
                  value={touchpointData.outcome}
                  onChange={(e) => setTouchpointData({ ...touchpointData, outcome: e.target.value })}
                  placeholder="e.g., Agreed to meeting, Requested proposal"
                />
              </div>
              <div>
                <Label>Next Action</Label>
                <StableInput
                  value={touchpointData.next_action}
                  onChange={(e) => setTouchpointData({ ...touchpointData, next_action: e.target.value })}
                  placeholder="e.g., Send proposal, Schedule call"
                />
              </div>
              <div>
                <Label>Next Action Date</Label>
                <StableInput
                  type="date"
                  value={touchpointData.next_action_date}
                  onChange={(e) => setTouchpointData({ ...touchpointData, next_action_date: e.target.value })}
                />
              </div>
              <Button onClick={handleAddTouchpoint} className="w-full bg-primary hover:bg-primary-800 text-white">
                Add Touchpoint
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
